import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { doadorApiCall } from './DoadorPortalLayout';
import { toast } from 'sonner';

export default function DoadorAlterarPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ atual: false, nova: false, confirmar: false });
  const [form, setForm] = useState({
    password_atual: '',
    nova_password: '',
    confirmar_password: ''
  });
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    };
    return checks;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validações
    const newErrors = {};
    
    if (!form.password_atual) {
      newErrors.password_atual = 'Password actual é obrigatória';
    }

    const checks = validatePassword(form.nova_password);
    if (!checks.length) {
      newErrors.nova_password = 'Mínimo 8 caracteres';
    }

    if (form.nova_password !== form.confirmar_password) {
      newErrors.confirmar_password = 'As passwords não coincidem';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const result = await doadorApiCall('/doador/alterar-password', {
        method: 'POST',
        body: JSON.stringify({
          password_atual: form.password_atual,
          nova_password: form.nova_password
        })
      });

      if (result.sucesso) {
        toast.success('Password alterada com sucesso!');
        navigate('/doador/dashboard');
      } else {
        setErrors({ password_atual: result.erro || 'Erro ao alterar password' });
      }
    } catch (error) {
      setErrors({ password_atual: error.message || 'Erro ao alterar password' });
    } finally {
      setLoading(false);
    }
  };

  const checks = validatePassword(form.nova_password);

  return (
    <div className="max-w-md mx-auto space-y-6" data-testid="doador-alterar-password-page">
      {/* Back */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/doador/dashboard')}
        className="text-slate-600"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar ao Dashboard
      </Button>

      <Card>
        <CardHeader className="text-center">
          <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <CardTitle>Alterar Password</CardTitle>
          <CardDescription>
            Mantenha a sua conta segura alterando a password regularmente
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password Atual */}
            <div className="space-y-2">
              <Label>Password Actual</Label>
              <div className="relative">
                <Input
                  type={showPasswords.atual ? 'text' : 'password'}
                  value={form.password_atual}
                  onChange={(e) => handleChange('password_atual', e.target.value)}
                  className={errors.password_atual ? 'border-red-500' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(p => ({...p, atual: !p.atual}))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPasswords.atual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password_atual && (
                <p className="text-xs text-red-500">{errors.password_atual}</p>
              )}
            </div>

            {/* Nova Password */}
            <div className="space-y-2">
              <Label>Nova Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.nova ? 'text' : 'password'}
                  value={form.nova_password}
                  onChange={(e) => handleChange('nova_password', e.target.value)}
                  className={errors.nova_password ? 'border-red-500' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(p => ({...p, nova: !p.nova}))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPasswords.nova ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.nova_password && (
                <p className="text-xs text-red-500">{errors.nova_password}</p>
              )}
              
              {/* Password strength */}
              {form.nova_password && (
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div className={`flex items-center gap-1 ${checks.length ? 'text-green-600' : 'text-slate-400'}`}>
                    {checks.length ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    Mínimo 8 caracteres
                  </div>
                  <div className={`flex items-center gap-1 ${checks.upper ? 'text-green-600' : 'text-slate-400'}`}>
                    {checks.upper ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    Uma maiúscula
                  </div>
                  <div className={`flex items-center gap-1 ${checks.lower ? 'text-green-600' : 'text-slate-400'}`}>
                    {checks.lower ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    Uma minúscula
                  </div>
                  <div className={`flex items-center gap-1 ${checks.number ? 'text-green-600' : 'text-slate-400'}`}>
                    {checks.number ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    Um número
                  </div>
                </div>
              )}
            </div>

            {/* Confirmar Password */}
            <div className="space-y-2">
              <Label>Confirmar Nova Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirmar ? 'text' : 'password'}
                  value={form.confirmar_password}
                  onChange={(e) => handleChange('confirmar_password', e.target.value)}
                  className={errors.confirmar_password ? 'border-red-500' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(p => ({...p, confirmar: !p.confirmar}))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPasswords.confirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmar_password && (
                <p className="text-xs text-red-500">{errors.confirmar_password}</p>
              )}
              {form.confirmar_password && form.nova_password === form.confirmar_password && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Passwords coincidem
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A alterar...
                </>
              ) : (
                'Alterar Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
